/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import React, { useState, useEffect, useMemo } from 'react';
import { SOC2Control, SOC2Category, ComplianceSecurityBalance, SOC2Scenario } from '../../types/soc2';
import { SOC2_CONTROLS, SOC2_CATEGORY_DESCRIPTIONS } from '../../constants/soc2Controls';
import { SOC2ControlManager } from '../../services/soc2ControlManager';

interface SOC2DashboardEnhancedProps {
  budget: number;
  onClose: () => void;
  onScenarioComplete?: (result: any) => void;
}

const SOC2DashboardEnhanced: React.FC<SOC2DashboardEnhancedProps> = ({
  budget,
  onClose,
  onScenarioComplete
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'controls' | 'scenarios' | 'balance' | 'analytics'>('overview');
  const [selectedCategory, setSelectedCategory] = useState<SOC2Category>('security');
  const [selectedControl, setSelectedControl] = useState<SOC2Control | null>(null);
  const [soc2Manager] = useState(() => new SOC2ControlManager());
  const [controls, setControls] = useState<SOC2Control[]>([]);
  const [balance, setBalance] = useState<ComplianceSecurityBalance | null>(null);
  const [scenarios, setScenarios] = useState<SOC2Scenario[]>([]);
  const [activeScenario, setActiveScenario] = useState<SOC2Scenario | null>(null);

  useEffect(() => {
    const initialControls = soc2Manager.getAllControls();
    setControls(initialControls);
    setBalance(soc2Manager.analyzeComplianceSecurityBalance());
    setScenarios(createDefaultScenarios());
  }, [soc2Manager]);

  const filteredControls = useMemo(() => {
    if (activeTab === 'controls') {
      return controls.filter(control => control.category === selectedCategory);
    }
    return controls;
  }, [controls, selectedCategory, activeTab]);

  const categoryStats = useMemo(() => {
    const stats: Record<SOC2Category, {
      total: number;
      implemented: number;
      averageEffectiveness: number;
      totalCost: number;
    }> = {
      security: { total: 0, implemented: 0, averageEffectiveness: 0, totalCost: 0 },
      availability: { total: 0, implemented: 0, averageEffectiveness: 0, totalCost: 0 },
      processing_integrity: { total: 0, implemented: 0, averageEffectiveness: 0, totalCost: 0 },
      confidentiality: { total: 0, implemented: 0, averageEffectiveness: 0, totalCost: 0 },
      privacy: { total: 0, implemented: 0, averageEffectiveness: 0, totalCost: 0 }
    };

    controls.forEach(control => {
      const category = control.category;
      stats[category].total++;
      if (control.implementation.status === 'implemented' ||
          control.implementation.status === 'operating_effectively') {
        stats[category].implemented++;
      }
      stats[category].averageEffectiveness += control.effectiveness;
      stats[category].totalCost += control.implementationCost;
    });

    Object.keys(stats).forEach(category => {
      const stat = stats[category as SOC2Category];
      stat.averageEffectiveness = stat.total > 0 ? Math.round(stat.averageEffectiveness / stat.total) : 0;
    });

    return stats;
  }, [controls]);

  const handleImplementControl = (controlId: string) => {
    const result = soc2Manager.implementControl(controlId, ['current_player'], budget);
    if (result.success) {
      const updatedControls = soc2Manager.getAllControls();
      setControls(updatedControls);
      setBalance(soc2Manager.analyzeComplianceSecurityBalance());
    }
  };

  const handleTestControl = (controlId: string) => {
    const result = soc2Manager.testControl(controlId);
    if (result.success) {
      const updatedControls = soc2Manager.getAllControls();
      setControls(updatedControls);
    }
  };

  const handleOptimizeBalance = () => {
    if (balance) {
      const targetRatio = 0.1; // Slight security emphasis
      const plan = soc2Manager.optimizeComplianceSecurityBalance(targetRatio);
      console.log('Optimization plan:', plan);
    }
  };

  const createDefaultScenarios = (): SOC2Scenario[] => [
    {
      id: 'audit-prep-basic',
      title: 'Basic Audit Preparation',
      description: 'Prepare for your first SOC 2 Type I audit',
      category: 'audit_preparation',
      difficulty: 'beginner',
      businessContext: {
        companySize: 'small',
        industry: 'fintech',
        regulatoryEnvironment: ['SOX', 'GLBA'],
        customerProfile: {
          sensitive: true,
          regulated: true,
          international: false,
          enterpriseLevel: false,
          dataVolume: 'medium'
        },
        riskProfile: { riskTolerance: 60, riskCapacity: 100000 },
        businessPriorities: ['growth', 'compliance', 'security']
      },
      threatLandscape: [],
      availableControls: ['cc1.1', 'cc6.1', 'cc4.1'],
      targetControls: ['cc1.1', 'cc6.1', 'cc4.1'],
      constraints: {
        budget: 150000,
        timeLimit: 120,
        availableResources: [],
        regulatoryRequirements: [],
        businessDisruption: 20,
        stakeholderExpectations: []
      },
      objectives: [
        { id: 'obj1', description: 'Implement required controls', targetValue: 3, currentValue: 0 },
        { id: 'obj2', description: 'Achieve 80% audit readiness', targetValue: 80, currentValue: 0 }
      ],
      successMetrics: [
        { id: 'metric1', name: 'Control Implementation', weight: 0.5 },
        { id: 'metric2', name: 'Cost Efficiency', weight: 0.3 },
        { id: 'metric3', name: 'Time Management', weight: 0.2 }
      ],
      timeLimit: 120,
      costBudget: 150000,
      riskTolerance: 40
    },
    {
      id: 'security-compliance-balance',
      title: 'Security vs. Compliance Balance',
      description: 'Balance security investments with compliance requirements',
      category: 'risk_assessment',
      difficulty: 'intermediate',
      businessContext: {
        companySize: 'medium',
        industry: 'saas',
        regulatoryEnvironment: ['GDPR', 'CCPA'],
        customerProfile: {
          sensitive: true,
          regulated: true,
          international: true,
          enterpriseLevel: true,
          dataVolume: 'high'
        },
        riskProfile: { riskTolerance: 50, riskCapacity: 500000 },
        businessPriorities: ['security', 'efficiency', 'growth']
      },
      threatLandscape: [],
      availableControls: ['cc1.1', 'cc6.1', 'cc5.1', 'cc3.1'],
      targetControls: [],
      constraints: {
        budget: 300000,
        timeLimit: 180,
        availableResources: [],
        regulatoryRequirements: [],
        businessDisruption: 30,
        stakeholderExpectations: []
      },
      objectives: [
        { id: 'obj1', description: 'Optimize security/compliance balance', targetValue: 0.1, currentValue: 0 },
        { id: 'obj2', description: 'Maintain business operations', targetValue: 90, currentValue: 100 }
      ],
      successMetrics: [
        { id: 'metric1', name: 'Balance Ratio', weight: 0.4 },
        { id: 'metric2', name: 'Security Score', weight: 0.3 },
        { id: 'metric3', name: 'Compliance Score', weight: 0.3 }
      ],
      timeLimit: 180,
      costBudget: 300000,
      riskTolerance: 50
    }
  ];

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(categoryStats).map(([category, stats]) => (
          <div key={category} className="bg-slate-800 rounded-lg p-6 border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white capitalize">
                {category.replace('_', ' ')}
              </h3>
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                stats.implemented === stats.total
                  ? 'bg-green-900/50 text-green-400 border border-green-600/30'
                  : 'bg-yellow-900/50 text-yellow-400 border border-yellow-600/30'
              }`}>
                {stats.implemented}/{stats.total}
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-400">Maturity</span>
                  <span className="text-white font-medium">{stats.averageEffectiveness}%</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full"
                    style={{ width: `${stats.averageEffectiveness}%` }}
                  ></div>
                </div>
              </div>
              <div className="text-sm">
                <span className="text-slate-400">Total Investment: </span>
                <span className="text-white font-medium">${(stats.totalCost / 1000).toFixed(0)}k</span>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              {SOC2_CATEGORY_DESCRIPTIONS[category as SOC2Category]}
            </p>
          </div>
        ))}
      </div>

      {balance && (
        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4">Security vs. Compliance Balance</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-400">Security Score</span>
                <span className="text-white font-medium">{balance.securityScore}%</span>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-3">
                <div
                  className="bg-red-500 h-3 rounded-full"
                  style={{ width: `${balance.securityScore}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-400">Compliance Score</span>
                <span className="text-white font-medium">{balance.complianceScore}%</span>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-3">
                <div
                  className="bg-blue-500 h-3 rounded-full"
                  style={{ width: `${balance.complianceScore}%` }}
                ></div>
              </div>
            </div>
          </div>
          <div className="mt-4 text-center">
            <span className="text-slate-400 text-sm">Balance Ratio: </span>
            <span className={`text-white font-medium ${
              balance.balanceRatio > 0.2 ? 'text-red-400' :
              balance.balanceRatio < -0.2 ? 'text-blue-400' : 'text-green-400'
            }`}>
              {balance.balanceRatio > 0 ? 'Security-heavy' : balance.balanceRatio < 0 ? 'Compliance-heavy' : 'Balanced'}
            </span>
          </div>
          <button
            onClick={handleOptimizeBalance}
            className="mt-4 w-full bg-purple-600 hover:bg-purple-500 text-white font-medium py-2 px-4 rounded transition-colors"
          >
            Optimize Balance
          </button>
        </div>
      )}
    </div>
  );

  const renderControls = () => (
    <div className="space-y-6">
      <div className="flex space-x-4 border-b border-slate-700">
        {Object.keys(SOC2_CATEGORY_DESCRIPTIONS).map((category) => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category as SOC2Category)}
            className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${
              selectedCategory === category
                ? 'text-blue-400 border-blue-400'
                : 'text-slate-400 border-transparent hover:text-slate-300'
            }`}
          >
            {category.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4">
        {filteredControls.map((control) => (
          <div key={control.id} className="bg-slate-800 rounded-lg p-6 border border-slate-700 hover:border-slate-600 transition-colors">
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h4 className="text-lg font-semibold text-white mb-1">
                  {control.number}: {control.title}
                </h4>
                <p className="text-sm text-slate-400 mb-3">{control.description}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {control.dependencies.map(dep => (
                    <span key={dep} className="text-xs px-2 py-1 bg-slate-700 text-slate-300 rounded">
                      Dep: {dep}
                    </span>
                  ))}
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                control.implementation.status === 'operating_effectively'
                  ? 'bg-green-900/50 text-green-400 border border-green-600/30'
                  : control.implementation.status === 'implemented'
                  ? 'bg-blue-900/50 text-blue-400 border border-blue-600/30'
                  : 'bg-gray-900/50 text-gray-400 border border-gray-600/30'
              }`}>
                {control.implementation.status.replace('_', ' ')}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <div className="text-xs text-slate-400 mb-1">Effectiveness</div>
                <div className="text-lg font-bold text-white">{control.effectiveness}%</div>
              </div>
              <div>
                <div className="text-xs text-slate-400 mb-1">Implementation</div>
                <div className="text-lg font-bold text-white">${(control.implementationCost / 1000).toFixed(0)}k</div>
              </div>
              <div>
                <div className="text-xs text-slate-400 mb-1">Risk Reduction</div>
                <div className="text-lg font-bold text-green-400">{control.riskReduction}%</div>
              </div>
              <div>
                <div className="text-xs text-slate-400 mb-1">Maintenance</div>
                <div className="text-lg font-bold text-yellow-400">${(control.maintenanceCost / 1000).toFixed(0)}k</div>
              </div>
            </div>

            <div className="flex gap-2">
              {control.implementation.status === 'not_implemented' && (
                <button
                  onClick={() => handleImplementControl(control.id)}
                  className="flex-1 bg-blue-600 hover:bg-blue-500 text-white font-medium py-2 px-4 rounded transition-colors"
                  disabled={budget < control.implementationCost}
                >
                  Implement (${(control.implementationCost / 1000).toFixed(0)}k)
                </button>
              )}
              {control.implementation.status === 'implemented' && (
                <button
                  onClick={() => handleTestControl(control.id)}
                  className="flex-1 bg-purple-600 hover:bg-purple-500 text-white font-medium py-2 px-4 rounded transition-colors"
                >
                  Test Control
                </button>
              )}
              <button
                onClick={() => setSelectedControl(control)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded transition-colors"
              >
                Details
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderScenarios = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {scenarios.map((scenario) => (
          <div key={scenario.id} className="bg-slate-800 rounded-lg p-6 border border-slate-700 hover:border-slate-600 transition-colors">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h4 className="text-lg font-semibold text-white mb-2">{scenario.title}</h4>
                <p className="text-sm text-slate-400 mb-3">{scenario.description}</p>
                <div className="flex gap-2 mb-3">
                  <span className={`px-2 py-1 text-xs rounded ${
                    scenario.difficulty === 'beginner' ? 'bg-green-900/50 text-green-400' :
                    scenario.difficulty === 'intermediate' ? 'bg-yellow-900/50 text-yellow-400' :
                    'bg-red-900/50 text-red-400'
                  }`}>
                    {scenario.difficulty}
                  </span>
                  <span className="px-2 py-1 text-xs bg-slate-700 text-slate-300 rounded">
                    {scenario.category.replace('_', ' ')}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div className="text-xs text-slate-400 mb-1">Time Limit</div>
                <div className="text-sm font-medium text-white">{scenario.timeLimit} minutes</div>
              </div>
              <div>
                <div className="text-xs text-slate-400 mb-1">Budget</div>
                <div className="text-sm font-medium text-white">${(scenario.costBudget / 1000).toFixed(0)}k</div>
              </div>
            </div>

            <div className="mb-4">
              <div className="text-xs text-slate-400 mb-2">Objectives:</div>
              <div className="space-y-1">
                {scenario.objectives.slice(0, 2).map((obj) => (
                  <div key={obj.id} className="text-xs text-slate-300">
                    • {obj.description}
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={() => setActiveScenario(scenario)}
              className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-medium py-2 px-4 rounded transition-colors"
            >
              Start Scenario
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  const renderBalance = () => (
    <div className="space-y-6">
      {balance && (
        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4">Balance Analysis</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-slate-900/50 rounded-lg p-4">
              <h4 className="text-white font-medium mb-3">Current Balance</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Security Score:</span>
                  <span className="text-white font-medium">{balance.securityScore}%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Compliance Score:</span>
                  <span className="text-white font-medium">{balance.complianceScore}%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Balance Ratio:</span>
                  <span className="text-white font-medium">{balance.balanceRatio.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Risk Score:</span>
                  <span className="text-white font-medium">{balance.riskScore}%</span>
                </div>
              </div>
            </div>

            <div className="bg-slate-900/50 rounded-lg p-4">
              <h4 className="text-white font-medium mb-3">Business Impact</h4>
              <div className="text-2xl font-bold text-green-400 mb-2">
                ${(balance.businessImpact / 1000).toFixed(0)}k
              </div>
              <div className="text-xs text-slate-400">
                Total business value from current controls
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-white font-medium mb-3">Recommendations</h4>
            <div className="space-y-2">
              {balance.recommendations.slice(0, 3).map((rec, index) => (
                <div key={index} className="bg-slate-900/30 rounded-lg p-3 border border-slate-700">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-sm font-medium text-white capitalize">
                      {rec.type.replace('_', ' ')}
                    </span>
                    <span className="text-xs px-2 py-1 bg-blue-900/50 text-blue-400 rounded">
                      {rec.controlId}
                    </span>
                  </div>
                  <div className="text-xs text-slate-400 mb-2">{rec.rationale}</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-slate-500">Impact:</span>
                      <span className="text-white ml-1">{rec.expectedImpact}%</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Cost:</span>
                      <span className="text-white ml-1">${(rec.cost / 1000).toFixed(0)}k</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
        <h3 className="text-lg font-semibold text-white mb-4">Optimization Opportunities</h3>
        <div className="space-y-3">
          {balance?.optimizationOpportunities.slice(0, 3).map((opp, index) => (
            <div key={index} className="bg-slate-900/50 rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <span className="text-sm font-medium text-white">{opp.controlId}</span>
                  <div className="text-xs text-slate-400">{opp.description}</div>
                </div>
                <span className="text-lg font-bold text-green-400">
                  ${(opp.potentialSavings / 1000).toFixed(0)}k
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Current: {opp.currentValue.toFixed(1)}</span>
                <span className="text-slate-500">Target: {opp.targetValue.toFixed(1)}</span>
                <span className="text-slate-500">Effort: {opp.implementationEffort}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderAnalytics = () => {
    const insights = soc2Manager.generateControlInsights();
    return (
      <div className="space-y-6">
        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4">Analytics</h3>
          <div className="text-white">Overall Maturity: {insights.overallMaturity}%</div>
          <div className="text-white">Critical Controls: {insights.criticalControls.length}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b border-slate-700 p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <span className="text-3xl">🔒</span>
                SOC 2 Compliance Center
              </h2>
              <p className="text-slate-400 mt-1">Interactive controls mapping and security-compliance balancing</p>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white text-2xl leading-none"
            >
              ×
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-700 px-6">
          <div className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview', icon: '📊' },
              { id: 'controls', label: 'Controls', icon: '🎛️' },
              { id: 'scenarios', label: 'Scenarios', icon: '🎮' },
              { id: 'balance', label: 'Balance', icon: '⚖️' },
              { id: 'analytics', label: 'Analytics', icon: '📈' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-white'
                    : 'border-transparent text-slate-400 hover:text-slate-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'controls' && renderControls()}
          {activeTab === 'scenarios' && renderScenarios()}
          {activeTab === 'balance' && renderBalance()}
          {activeTab === 'analytics' && renderAnalytics()}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-700 p-4">
          <div className="flex justify-between items-center">
            <div className="text-sm text-slate-400">
              Budget: <span className="text-white font-medium">${(budget / 1000).toFixed(0)}k</span>
            </div>
            <div className="flex gap-2">
              <button className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded transition-colors">
                Export Report
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SOC2DashboardEnhanced;